import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Panel, Margin, Text } from 'shared/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useHistory } from 'react-router-dom'
import { BottomNavigation } from '../../public/events/overview/components/bottomNavigation'
import InWinNotification from 'shared/components/inwinNotification'

import * as styles from './styles'

const DonationReceiptsComponent = ({
	loggedEntity,
	donationYears,
	loadingStates,
	onClickDownload
}) => {
	const history = useHistory()
	const [showTooltip, setShowTooltip] = useState(false)

	const handleBackClick = () => {
		history.push('/profile')
	}

	const handleTooltipClick = () => {
		setShowTooltip(true)
		// Hide tooltip after 8 seconds (increased from default)
		setTimeout(() => {
			setShowTooltip(false)
		}, 8000)
	}

	// Hide tooltip when clicking outside or when component unmounts
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (showTooltip) {
				setShowTooltip(false)
			}
		}

		if (showTooltip) {
			document.addEventListener('click', handleClickOutside)
			return () => document.removeEventListener('click', handleClickOutside)
		}
	}, [showTooltip])

	if (!loggedEntity) {
		return (
			<>
				{/* InWin Notifications */}
				<InWinNotification />

				{/* Mobile Header */}
				<div css={styles.mobileHeader}>
					<div css={styles.headerContainer}>
						<FontAwesomeIcon
							icon="arrow-left"
							css={styles.backArrow}
							onClick={handleBackClick}
						/>
						<div css={styles.titleContainer}>
							<Text size={16} bold css={styles.headerTitle}>
								Donation Receipts
							</Text>
						</div>
					</div>
				</div>

				<Panel css={styles.panel}>
					<div css={styles.contentWrapper}>
						<Text>Loading...</Text>
					</div>
				</Panel>

				<BottomNavigation />
			</>
		)
	}

	const { firstName, lastName, address } = loggedEntity

	// More explicit name validation
	const isNameFilled = Boolean(firstName?.trim()) && Boolean(lastName?.trim())

	// More explicit address validation with individual checks
	const hasAddressLine1 = Boolean(address?.address1?.trim())
	const hasCity = Boolean(address?.city?.trim())
	const hasPostalCode = Boolean(address?.zip?.toString().trim())
	const hasCountry = Boolean(address?.country?.trim())

	const isAddressFilled = hasAddressLine1 && hasCity && hasPostalCode && hasCountry

	const listOfReceipts = donationYears.map((year) => {
		const handleDownloadClick = (e) => {
			e.stopPropagation()
			e.preventDefault()
			if (!isAddressFilled) {
				handleTooltipClick()
				return
			}
			onClickDownload(year)
			
		}

		const handleEmailClick = (e) => {
			e.stopPropagation()
			e.preventDefault()
			if (!isAddressFilled) {
				handleTooltipClick()
				return
			}
			onClickDownload(year, true)
			
		}

		const buttons = (
			<div css={styles.buttonGroup}>
				<button
					type="button"
					css={[
						styles.iconButton,
						styles.downloadButton,
						(!isAddressFilled || loadingStates[year]?.download) && styles.disabledButton
					]}
					onClick={handleDownloadClick}
					aria-label={loadingStates[year]?.download ? 'Downloading...' : 'Download PDF'}
				>
					{loadingStates[year]?.download ? (
						<div css={styles.loadingSpinner} />
					) : (
						<FontAwesomeIcon icon="download" />
					)}
				</button>
				<button
					type="button"
					css={[
						styles.iconButton,
						styles.emailButton,
						(!isAddressFilled || loadingStates[year]?.email) && styles.disabledButton
					]}
					onClick={handleEmailClick}
					aria-label={loadingStates[year]?.email ? 'Requesting...' : 'Request Email'}
				>
					{loadingStates[year]?.email ? (
						<div css={styles.loadingSpinner} />
					) : (
						<FontAwesomeIcon icon="envelope" />
					)}
				</button>
			</div>
		)

		return (
			<div key={year} css={styles.receiptRow}>
				<div css={styles.receiptInfo}>
					<Text size={12} bold css={styles.yearText}>
						{`${year}`}
					</Text>
				</div>
				<div css={styles.receiptActions}>
					{buttons}
				</div>
			</div>
		)
	})

	return (
		<>
			{/* Custom Mobile Tooltip */}
			{showTooltip && (
				<div css={styles.mobileTooltipOverlay} onClick={() => setShowTooltip(false)}>
					<div css={styles.mobileTooltip} onClick={(e) => e.stopPropagation()}>
						<Text white size={12}>
							Add Address in your Personal information to download or request by email your donation receipts.
						</Text>
					</div>
				</div>
			)}

			{/* InWin Notifications */}
			<InWinNotification />

			{/* Mobile Header */}
			<div css={styles.mobileHeader}>
				<div css={styles.headerContainer}>
					<FontAwesomeIcon
						icon="arrow-left"
						css={styles.backArrow}
						onClick={handleBackClick}
					/>
					<div css={styles.titleContainer}>
						<Text size={16} bold css={styles.headerTitle}>
							Donation Receipts
						</Text>
					</div>
				</div>
			</div>

			<Panel css={styles.panel}>
				<div css={styles.contentWrapper}>
					<Margin bottom={5} css={styles.title}>
						<Text as="h1" size={18} bold>
							Donation Receipts
						</Text>
					</Margin>

					<div css={styles.pageWrapper}>
						<div css={styles.formContent}>
							{donationYears.length > 0 ? (
								<>
									<div css={styles.receiptsContainer}>
										{listOfReceipts}
									</div>
								</>
							) : (
								<div css={styles.emptyState}>
									<Text size={14} css={styles.emptyText}>
										You don't have any donation receipts yet. Play InWins to make donations.
									</Text>
								</div>
							)}
						</div>
					</div>
				</div>
			</Panel>

			<BottomNavigation />
		</>
	)
}

DonationReceiptsComponent.propTypes = {
	loggedEntity: PropTypes.shape(),
	donationYears: PropTypes.arrayOf(PropTypes.number),
	loadingStates: PropTypes.objectOf(PropTypes.shape({
		download: PropTypes.bool,
		email: PropTypes.bool
	})),
	onClickDownload: PropTypes.func,
}

DonationReceiptsComponent.defaultProps = {
	donationYears: [],
}

export default DonationReceiptsComponent
